import { useParams, Link, Navigate } from 'react-router-dom';
import PageHero from '../components/ui/PageHero';
import Reveal from '../components/ui/Reveal';
import { features } from '../data/features';
import { modules } from '../data/modules';
import { industries } from '../data/industries';
import './FeatureDetail.css';

export default function FeatureDetail() {
  const { slug } = useParams();
  const feature = features.find((f) => f.slug === slug);

  if (!feature) {
    return <Navigate to="/platform" replace />;
  }

  const Icon = feature.icon;
  const others = features.filter((f) => f.slug !== slug).slice(0, 6);
  const relatedModules = modules.filter((m) => feature.connectedModules?.includes(m.id));
  const relatedIndustries = industries.filter((ind) => feature.industries?.includes(ind.id));

  return (
    <div className="feature-detail-page">
      <PageHero
        eyebrow="Solutions"
        title={feature.name}
        body={feature.tagline}
      />

      <section className="section-tight feature-detail-intro">
        <div className="container feature-detail-intro-grid">
          <Reveal as="div" className="feature-detail-icon-block">
            <div className="feature-detail-icon"><Icon size={44} strokeWidth={1.4} /></div>
          </Reveal>
          <Reveal as="div" className="feature-detail-description-block">
            <p className="feature-detail-description">{feature.description}</p>
          </Reveal>
          {feature.stat && (
            <Reveal as="div" className="feature-detail-stat">
              <strong>{feature.stat.value}</strong>
              <span>{feature.stat.label}</span>
            </Reveal>
          )}
        </div>
      </section>

      {feature.steps && (
        <section className="section section-alt">
          <div className="container">
            <Reveal className="section-head">
              <span className="eyebrow">How It Works</span>
              <h2>From first touch to done, in three steps.</h2>
            </Reveal>

            <Reveal as="div" stagger className="feature-steps">
              {feature.steps.map((step, i) => (
                <div key={step.title} className="feature-step reveal-item">
                  <span className="feature-step-number">{String(i + 1).padStart(2, '0')}</span>
                  <h3>{step.title}</h3>
                  <p>{step.detail}</p>
                </div>
              ))}
            </Reveal>
          </div>
        </section>
      )}

      <section className="section">
        <div className="container feature-detail-grid">
          <Reveal as="div" className="feature-detail-main">
            <span className="eyebrow">What You Get</span>
            <ul className="feature-detail-points">
              {feature.points.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>

            {relatedIndustries.length > 0 && (
              <div className="feature-detail-industries">
                <span className="feature-detail-subhead">Built for</span>
                <div className="feature-detail-industry-chips">
                  {relatedIndustries.map((ind) => (
                    <Link key={ind.id} to="/industries" className="feature-industry-chip">
                      <ind.icon size={16} strokeWidth={1.8} />
                      {ind.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <div className="hero-actions">
              <Link to="/contact" className="btn btn-primary">Book a Demo</Link>
              <Link to="/platform" className="btn btn-secondary">See the full platform</Link>
            </div>
          </Reveal>

          <Reveal as="div" className="feature-detail-side">
            {relatedModules.length > 0 && (
              <>
                <span className="eyebrow">Part Of</span>
                <ul className="feature-detail-related feature-detail-modules">
                  {relatedModules.map((m) => (
                    <li key={m.id}>
                      <Link to="/platform">
                        <m.icon size={18} strokeWidth={1.8} />
                        {m.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </>
            )}

            <span className="eyebrow feature-detail-explore">Explore More</span>
            <ul className="feature-detail-related">
              {others.map((f) => (
                <li key={f.slug}>
                  <Link to={`/solutions/${f.slug}`}>
                    <f.icon size={18} strokeWidth={1.8} />
                    {f.name}
                  </Link>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="section cta-section">
        <div className="container">
          <Reveal className="cta-box card">
            <h2>See {feature.name.toLowerCase()} running on your workflow.</h2>
            <p>Book a walkthrough and we’ll show you exactly how it fits your business.</p>
            <div className="hero-actions center">
              <Link to="/contact" className="btn btn-primary">Book a Demo</Link>
              <Link to="/industries" className="btn btn-secondary">See it by industry</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
